/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, {
  useState, useEffect, useRef, useMemo,
} from 'react';

// Sagas
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';

import { Field } from 'formik';

import {
  Table, Thead, Tbody, Tr, Th, Td,
} from 'react-super-responsive-table';

import {
  Button,
  Spinner,
  Alert,
  Row,
  Col,
  Badge,
  UncontrolledTooltip,
} from 'reactstrap';
import { Link } from 'react-router-dom';
import FilterDropdown from '../CommonForBoth/FilterDropdown';
import BasePagination from './BasePagination';
import { get } from '../../helpers/api_helper';
import 'react-super-responsive-table/dist/SuperResponsiveTableStyle.css';

const columnDefinition = {
  label: '',
  sort: false,
  link: false,
  filter: false,
  format: (x) => x,
  badges: false,
  date: false,
  actions: false,
  translate: true,
  image: false,
  modcfg: false,
  html: false,
  style: {},
  class: '',
};

function BaseGrid(props) {
  const {
    url,
    config,
    hasSearch,
    search,
    list,
    skipSort,
    createText,
    filters,
    onDataLoaded,
    rowUpdate,
    ...rest
  } = props;

  let { keyProp } = props;
  const { shouldUpdate } = props;

  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(config.pagination.perPage);
  const [sort, setSort] = useState(config.pagination.sort);
  const [dir, setDir] = useState(config.pagination.direction);
  const [data, setData] = useState({ results: [], total: 0 });
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [query, setQuery] = useState(search);
  const [filter, setFilter] = useState(null);
  const [signal, setSignal] = useState(0);
  const gridRef = useRef(null);
  const rowChanges = useRef(new Map());

  const className = rest.className || 'table table-striped table-bordered';

  if (keyProp == null) keyProp = 'id';

  const manageColumns = async (key, state) => {
    try {
      // Set the visibility in localStorage
      const colKey = `gridcols_${url.replace(/[^a-zA-Z0-9]/g, '_')}`;
      const cols = localStorage.getItem(colKey);
      const colsObj = {};
      if (cols != null) {
        const colsArr = cols.split(',');
        for (let i = 0; i < colsArr.length; i += 1) {
          colsObj[colsArr[i]] = true;
        }
      }
      if (state === 0) {
        // remove
        colsObj[key] = true;
      } else {
        delete colsObj[key];
      }
      const newCols = Object.keys(colsObj).join(',');
      localStorage.setItem(colKey, newCols);
    } catch (e) {
      // do nothing
    }

    setSignal((value) => value + 1);
  };

  const hiddenColumns = useMemo(() => {
    const cols = localStorage.getItem(`gridcols_${url.replace(/[^a-zA-Z0-9]/g, '_')}`);
    return new Set(cols ? cols.split(',') : []);
  }, [url, signal]);

  const isColumnFixed = (key) => {
    const fixed = config.columns[key].fixed !== undefined ? config.columns[key].fixed : false;
    return fixed === true;
  };

  const isColumnVisible = (key) => {
    if (isColumnFixed(key)) {
      return true;
    }
    return !hiddenColumns.has(key);
  };

  // Merge confirmed saves without losing the current page, sort, or scroll position.
  useEffect(() => {
    if (!rowUpdate) return;
    const id = rowUpdate[keyProp];
    const changes = { ...rowChanges.current.get(id), ...rowUpdate };
    rowChanges.current.set(id, changes);
    setData((current) => ({
      ...current,
      results: current.results.map((row) => (
        row[keyProp] === id ? { ...row, ...rowUpdate } : row
      )),
    }));
  }, [rowUpdate, keyProp]);

  useEffect(() => {
    let active = true;
    rowChanges.current.clear();
    const fetchData = async (p, s, d, pp, q, f) => {
      let uri;

      if (q != null) {
        uri = `${url}?page=${p}&sort=${s}&dir=${d}&per=${pp}&q=${encodeURIComponent(q)}&f=${encodeURIComponent(f)}`;
      } else {
        uri = `${url}?page=${p}&sort=${s}&dir=${d}&per=${pp}&f=${encodeURIComponent(f)}`;
      }

      if (list !== false) {
        uri = `${uri}&list=${list.join(',')}`;
      }

      if (list !== false && list[0] == null) {
        setLoading(false);
        setData({ total: 0, results: [] });
        setTotalPages(0);
        return;
      }

      try {
        const rawData = await get(uri);
        if (!active) return;
        // A request started before a save must not overwrite that confirmed save.
        const results = rawData.results.map((row) => {
          const update = rowChanges.current.get(row[keyProp]);
          return update ? { ...row, ...update } : row;
        });

        setData({ ...rawData, results });
        if (onDataLoaded !== undefined) {
          onDataLoaded(results);
        }
        setTotalPages(Math.ceil(rawData.total / perPage));
        setSort(s);
        setDir(d);
        setPerPage(pp);
      } catch (e) {
        if (active) setError(e.message);
      } finally {
        if (active) setLoading(false);
      }
    };

    setLoading(true);
    setError(false);
    fetchData(currentPage, sort, dir, perPage, query, filters || filter);
    return () => { active = false; };
  }, [url, currentPage, sort, dir, query, shouldUpdate, filters, filter]);

  useEffect(() => {
    if (search !== '' && search !== null) setQuery(search);
  }, [search]);

  const changeSort = (key) => {
    if (sort === key) {
      setDir((prevDir) => (prevDir === 'asc' ? 'desc' : 'asc'));
    } else {
      setSort(key);
      setCurrentPage(1);
    }
  };

  const searchKeyUp = (event) => {
    if (event.which === 13) {
      setCurrentPage(() => 1);
      setQuery(() => event.target.value);
    }
  };

  const goToPage = (page) => {
    setCurrentPage(page);

    if (gridRef.current != null) {
      gridRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  };

  /**
   * Prepare column header from config
   *
   * @param {*} currentSort active sort column key
   * @param {*} currentDir current sort direction
   * @param {*} inc increment
   * @param {*} key data key for intended column
   * @param {*} column the custom column config
   * @returns jsx
   */
  function prepHeader(currentSort, currentDir, inc, key, column) {
    const mc = { ...columnDefinition, ...column };

    let jsx;
    let sortButton;
    const colkey = `gridheader_${key}`;

    if (!skipSort && mc.sort !== false) {
      if (currentSort === key) {
        // this column is the current sort
        const classname = currentDir === 'asc' ? 'bx bx-chevron-up' : 'bx bx-chevron-down';

        sortButton = (
          <strong className={classname} style={{ verticalAlign: 'middle' }} />
        );
      }

      jsx = (
        <Th className="position-relative align-middle" style={{ ...mc.style, paddingRight: '20px' }} data-priority={inc} key={colkey}>
          <Button
            color="link"
            style={
              {
                padding: '0px',
                fontWeight: '600',
              }
            }
            onClick={() => changeSort(key)}
          >
            {props.t(mc.label)}
          </Button>

          {sortButton}
          {!isColumnFixed(key) && (
            <div className="right-checkbox-container form-check form-check-info">
              <input type="checkbox" className="form-check-input" checked onChange={() => { manageColumns(key, 0); }} />
            </div>
          )}
        </Th>
      );
    } else {
      jsx = (
        <Th className="position-relative align-middle" style={{ ...mc.style, paddingRight: '20px' }} data-priority={inc} key={colkey}>
          <>
            {mc.label}
            <div className="right-checkbox-container form-check form-check-info">
              <input type="checkbox" className="form-check-input" checked onChange={() => { manageColumns(key, 0); }} />
            </div>
          </>
        </Th>
      );
    }

    return jsx;
  }

  /**
   * Prepare column using config
   */
  function prepColumn(key, column, item) {
    const mc = { ...columnDefinition, ...column };
    let jsx;

    // format value
    let final = mc.format(item[key], item);

    // translate
    if (mc.translate) {
      if (typeof final === 'string') {
        final = props.t(final);
      } else if (Array.isArray(final)) {
        for (let i = 0; i < final.length; i += 1) {
          final[i] = props.t(final[i]);
        }
      }
    }

    // Formik Field
    if (mc.field !== undefined) {
      // const { values } = useFormikContext(); // Get Formik context here

      final = (
        <Field
          name={mc.field.name(item)}
          type={mc.field.type !== undefined ? mc.field.type : 'text'}
          className="form-control form-control-sm"
          placeholder={mc.field.placeholder !== undefined ? props.t(mc.field.placeholder) : ''}
        />
      );
    }

    // Date
    if (mc.date !== false && item[key] != null) {
      // Helper function to get the ordinal suffix for a number
      const getOrdinalSuffix = (day) => {
        if (day > 3 && day < 21) return 'th'; // For 11th, 12th, 13th, etc.
        switch (day % 10) {
          case 1: return 'st';
          case 2: return 'nd';
          case 3: return 'rd';
          default: return 'th';
        }
      };

      const d = new Date(item[key]);

      const tooltipContent = mc.date.time === false
        ? `${new Intl.DateTimeFormat('en-US', { weekday: 'long', timeZone: 'UTC' }).format(d)} the ${d.getUTCDate()}${getOrdinalSuffix(d.getUTCDate())}`
        : d.toString();

      final = (
        <>
          <span id={`date_${item[keyProp]}_${key}`}>{mc.date.format(d)}</span>
          <UncontrolledTooltip placement="top" target={`date_${item[keyProp]}_${key}`}>
            {tooltipContent}
          </UncontrolledTooltip>
        </>
      );
    }

    // Image
    if (mc.image !== false) {
      const src = mc.image.src(item);

      final = (
        <img alt="thumbnail" src={src} className={mc.image.class} style={mc.image.style} />
      );
    }

    // Badges
    if (mc.badges !== false) {
      let shrap = final;

      if (final !== null) {
        if (typeof mc.badges.split === 'string') {
          shrap = final.toString().split(mc.badges.split);
        }

        final = [];

        for (let i = 0; i < shrap.length; i += 1) {
          final.push(
            <span key={`badge_${item.id}_${i}`} className={`badge ${mc.badges.class}`}>
              {shrap[i]}
            </span>,
            <span key={`badge_${item.id}_${i}_ws`}> </span>, // add whitespace
          );
        }
      }
    }

    // html
    if (mc.html !== false) {
      // eslint-disable-next-line react/no-danger
      final = <div dangerouslySetInnerHTML={{ __html: final }} />;
    }

    jsx = <Td className={`align-middle ${mc.class}`} style={mc.style} key={key}>{final}</Td>;

    // Actions
    if (mc.actions !== false) {
      jsx = [];
      for (let i = 0; i < mc.actions.length; i += 1) {
        if (mc.actions[i].condition === undefined || mc.actions[i].condition(item) === true) {
          jsx.push(
            <li key={`li_${item.id}_${mc.actions[i].key}`} style={{ margin: 'auto' }}>
              <Button
                id={`act_btn_${item.id}_${mc.actions[i].key}`}
                alt={mc.actions[i].alt}
                color="primary"
                className={mc.actions[i].class}
                disabled={(mc.actions[i].disabled)}
                onClick={(event) => mc.actions[i].click(item, event)}
              >
                <span className={mc.actions[i].icon} />
              </Button>
            </li>,
            <UncontrolledTooltip key={mc.actions[i].key} placement="top" target={`act_btn_${item.id}_${mc.actions[i].key}`}>
              {mc.actions[i].alt}
            </UncontrolledTooltip>,
          );
        }
      }

      jsx = (
        <Td className="align-middle" key={key}>
          <ul key={`ul_${item.id}`} className="list-unstyled hstack gap-1 mb-0">
            {jsx}
          </ul>
        </Td>
      );
    }

    // Links
    if (mc.link !== false) {
      if (mc.link.func !== undefined) {
        const lnkUrl = mc.link.func(item);
        jsx = (
          <Td className="align-middle" key={key}>
            <a
              href={lnkUrl}
            >
              {final}
            </a>
          </Td>
        );
      } else if (mc.link.click !== undefined) {
        jsx = (
          <Td className="align-middle" key={key}>
            <Button
              color="link"
              className="p-1"
              onClick={() => mc.link.click(item)}
            >
              {final}
            </Button>
          </Td>
        );
      } else if (mc.link.condition === undefined || mc.link.condition(item) !== false) {
        if (mc.link.external !== true) {
          jsx = (
            <Td className="align-middle" key={key}>
              <Link
                to={mc.link.to.replace(/%(.+?)%/g, (mat, grp) => `${item[grp]}`)}
              >
                {final}
              </Link>
            </Td>
          );
        } else {
          jsx = (
            <Td key={key}>
              <a
                target="_blank"
                rel="noreferrer"
                href={mc.link.to.replace(/%(.+?)%/g, (mat, grp) => `${item[grp]}`)}
              >
                {final}
              </a>
            </Td>
          );
        }
      }
    }

    if (mc.filter !== false) {
      jsx = (
        <Td className="align-middle" key={key}>
          <Button
            color="link"
            onClick={() => {
              setFilter(new URLSearchParams(mc.filter(item)).toString());
            }}
            size="sm"
          >
            {final}
          </Button>
        </Td>
      );
    }

    return jsx;
  }

  let searchField;
  let searchQuery;
  let createButton;
  let destroyFilter;

  if (hasSearch) {
    searchField = (
      <Col md="4">
        <div className="app-search d-lg-block">
          <div className="position-relative">
            <input
              type="text"
              className="form-control"
              placeholder={props.t('Search...')}
              onKeyUp={searchKeyUp}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  return false;
                }

                return true;
              }}
            />
            <span className="bx bx-search-alt" />
          </div>
        </div>
      </Col>
    );

    if (query != null) {
      searchQuery = (
        <Badge style={{ fontSize: '12px', padding: '5px' }} color="info">
          <strong className="bx bx-search" />
          &nbsp;&nbsp;
          {query}
          &nbsp;&nbsp;
          <span style={{ cursor: 'pointer' }} onClick={() => { setQuery(null); return false; }} className="mdi mdi-close-thick" />
        </Badge>
      );
    }

    if (filters === '' && filter !== null) {
      destroyFilter = (
        <Badge style={{ fontSize: '12px', padding: '5px' }} color="info">
          <strong className="bx bx-filter" />
          &nbsp;&nbsp;
          {props.t('Filter')}
          &nbsp;&nbsp;
          <span style={{ cursor: 'pointer' }} onClick={() => { setFilter(null); return false; }} className="mdi mdi-close-thick" />
        </Badge>
      );
    }
  }

  if (props.create != null && typeof props.create === 'string') {
    createButton = (
      <Link to={props.create} className="btn btn-primary btn-sm">
        <span className="bx bx-plus-medical" />
        &nbsp;
        <span>{props.t(createText)}</span>
      </Link>
    );
  } else if (props.create != null && typeof props.create === 'function') {
    createButton = (
      <Button color="primary" className="btn btn-sm btn-primary" onClick={props.create}>
        <span className="bx bx-plus" />
        &nbsp;
        <span>{props.t(createText)}</span>
      </Button>
    );
  }

  let tableBody;
  if (loading === true) {
    tableBody = (
      <Tr>
        <Td colSpan={99}>
          <div className="text-center">
            <Spinner style={{ width: '4rem', height: '4rem' }} type="border" color="info" />
          </div>
        </Td>
      </Tr>
    );
  } else if (data.total === 0 && loading === false) {
    if (error !== false) {
      tableBody = (
        <Tr>
          <td colSpan={99}>
            <Alert color="danger" className="mt-3" isOpen>
              {error}
            </Alert>
          </td>
        </Tr>
      );
    } else {
      tableBody = (
        <Tr>
          <td className="align-middle" colSpan={99}>
            <p className="mt-1 mb-0">{props.t('No Results.')}</p>
          </td>
        </Tr>
      );
    }
  } else {
    tableBody = (
      data.results.map((item) => (
        <Tr className={config.rowClass !== undefined ? config.rowClass(item) : ''} key={item[keyProp]}>
          {(() => Object.keys(config.columns).map((x) => {
            const isVisible = isColumnVisible(x);
            if (isVisible) {
              return prepColumn(x, config.columns[x], item);
            }
            return null;
          }))()}
        </Tr>
      ))
    );
  }

  const jsx = (
    <div ref={gridRef}>
      <Row>
        <Col md={hasSearch ? '7' : '11'}>
          <div className={hasSearch ? 'hstack gap-3 mt-4' : 'hstack gap-3 mb-2'}>
            {props.shouldPaginate && data.total > 0 && (
              <div className="bg-light border text" style={{ padding: '4px' }}>
                {`${Math.floor(perPage * (currentPage - 1) + 1).toString()}-`}
                {`${Math.floor(perPage * (currentPage - 1) + data.results.length).toString()} of ${data.total}`}
              </div>
            )}
            {createButton}
            {searchQuery}
            {destroyFilter}
          </div>
        </Col>
        <Col md="1" className="d-flex justify-content-end">
          <FilterDropdown
            columns={config.columns}
            isColumnVisible={isColumnVisible}
            isColumnFixed={isColumnFixed}
            manageColumns={manageColumns}
            t={props.t}
          />
        </Col>

        {searchField}
      </Row>

      <div className="table-rep-plugin">
        <div
          className="table-responsive mb-0"
          data-pattern="priority-columns"
        >
          <Table
            className={className}
          >
            <Thead>
              <Tr>
                {(() => Object.keys(config.columns).map((x, i) => {
                  const isVisible = isColumnVisible(x);
                  if (isVisible) {
                    return prepHeader(sort, dir, i, x, config.columns[x]);
                  }
                  return null;
                }))()}
              </Tr>
            </Thead>
            <Tbody>
              {tableBody}
            </Tbody>
          </Table>
          {props.shouldPaginate && (
            <div className="float-end">
              <BasePagination
                lastPage={totalPages}
                currentPage={currentPage}
                switchPage={goToPage}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return jsx;
}

BaseGrid.defaultProps = {
  shouldUpdate: 0,
  shouldPaginate: true,
  hasSearch: false,
  create: false,
  createText: 'New',
  keyProp: 'id',
  search: null,
  t: (x) => (x),
  list: false,
  skipSort: false,
  filters: '',
  onDataLoaded: undefined,
  rowUpdate: null,
};

BaseGrid.propTypes = {
  url: PropTypes.string.isRequired,
  hasSearch: PropTypes.bool,
  create: PropTypes.any,
  createText: PropTypes.string,
  keyProp: PropTypes.string,
  search: PropTypes.string,
  config: PropTypes.object.isRequired,
  t: PropTypes.any,
  shouldUpdate: PropTypes.number,
  shouldPaginate: PropTypes.bool,
  list: PropTypes.any,
  filters: PropTypes.string,
  skipSort: PropTypes.bool,
  onDataLoaded: PropTypes.func,
  rowUpdate: PropTypes.object,
};

export default connect(() => ({

}), {})(withTranslation()(BaseGrid));
